/**
 * CitationVisibilityContext — Session-scoped toggle for inline `[N]` markers.
 *
 * PR-AAU-B (Ask Argus Pass 1):
 *   The Ask Argus composer carries a single `Citations [ON / OFF]` toggle at
 *   the bottom of the chat. The state is session-scoped — flipping it affects
 *   every visible assistant bubble in the current chat surface, but does NOT
 *   touch the right-side context panel (which always shows citations).
 *
 *   ON  (default) — bubbles show inline numbered markers `[1] [2] [3]…`
 *   OFF           — bubbles strip those markers so copy-paste is clean
 *
 *   The right `ContextPanel` reads from `Message.citations` directly and is
 *   unaffected by this flag, per the founder spec (2026-05-12 walkthrough).
 *
 * Why a context (not just a prop):
 *   The composer (producer) and message list (consumer) are siblings under
 *   `AskArgusPage`. A context lets us avoid prop-drilling through
 *   `ChatMessages` and keeps the API surface for `ChatInput` small.
 *
 * Why not in `chat-tools-strip`:
 *   The tools strip is per-conversation server-persisted state. Citation
 *   visibility is a pure-UI per-session preference and does not need to
 *   round-trip to the backend.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface CitationVisibilityContextValue {
  /** True when inline `[N]` markers should be rendered in bubble bodies. */
  citationsVisible: boolean;
  /** Flip the visibility. */
  toggle: () => void;
  /** Explicit setter for tests + future programmatic use. */
  setCitationsVisible: (next: boolean) => void;
}

const CitationVisibilityContext =
  createContext<CitationVisibilityContextValue | null>(null);

export function CitationVisibilityProvider({ children }: { children: ReactNode }) {
  const [citationsVisible, setCitationsVisible] = useState<boolean>(true);

  const toggle = useCallback(() => {
    setCitationsVisible((prev) => !prev);
  }, []);

  const value = useMemo<CitationVisibilityContextValue>(
    () => ({ citationsVisible, toggle, setCitationsVisible }),
    [citationsVisible, toggle],
  );

  return (
    <CitationVisibilityContext.Provider value={value}>
      {children}
    </CitationVisibilityContext.Provider>
  );
}

/**
 * Hook for the producer (composer toggle) and the consumer (message
 * bubble). Falls back to a "citations visible" no-op when used outside
 * the provider so the components remain renderable in isolation (tests,
 * Storybook). The fallback also means `chat-messages` can be reused
 * outside the Ask Argus shell without crashing.
 */
export function useCitationVisibility(): CitationVisibilityContextValue {
  const ctx = useContext(CitationVisibilityContext);
  if (ctx) return ctx;
  // No-op default — citations always visible if no provider mounted.
  return {
    citationsVisible: true,
    toggle: () => {
      // intentional no-op
    },
    setCitationsVisible: () => {
      // intentional no-op
    },
  };
}

/**
 * Strip inline `[N]` citation markers from a string. Used by the message
 * bubble when citations are hidden. Pure function — easy to unit-test.
 *
 *   - Matches `[1]`, `[12]`, `[ 3 ]`, etc.
 *   - Also strips the `[AI]` / `[WEB]` markers from the Library + AI
 *     knowledge-mode addendum, since those are the same convention.
 *   - Collapses double spaces left behind by removed markers, but
 *     preserves newlines + paragraph structure.
 *
 * Exported so tests can hit it without rendering React.
 */
export function stripCitationMarkers(content: string): string {
  if (!content) return content;
  return content
    // numeric + `[Source N]` markers, ASCII `[…]` AND full-width `【…】` (CJK
    // models emit the latter). This is a SUPERSET of the injector grammar in
    // MarkdownRenderer (`/([[【](?:Source\s*)?\d+[\]】])/g`) — it covers the
    // canonical `[Source N]` / `【Source N】` the backend actually emits (the
    // earlier bare `[\d+]`-only pattern missed those, so OFF stripped nothing),
    // and tolerates inner whitespace (`[ 3 ]`) for clean copy-paste.
    .replace(/[[【]\s*(?:Source\s*)?\d+\s*[\]】]/gi, '')
    // bug #5 — the OFF toggle must strip the SAME three marker forms the
    // injector renders, or the asymmetry shows: a Library "[2]" vanished while
    // a Web "[Web 4]" degraded to raw text. Add the web-tier + tool-name forms.
    .replace(/[[【]\s*Web\s*\d+\s*[\]】]/gi, '')
    .replace(/[[【][^[\]【】]*(?:ext|iq|forge)\.[a-zA-Z][a-zA-Z0-9_.]*[^[\]【】]*[\]】]/gi, '')
    // narrative markers used by Library + AI mode (`[AI]`/`[WEB]`, full-width too)
    .replace(/[[【]\s*(AI|WEB)\s*[\]】]/gi, '')
    // tighten the gaps the removal leaves behind (but not newlines)
    .replace(/[ \t]{2,}/g, ' ')
    // trim trailing whitespace on lines without touching the line endings
    .replace(/[ \t]+(\r?\n)/g, '$1')
    // and trim end-of-string whitespace introduced by a final marker
    .replace(/[ \t]+$/g, '');
}
